import { DateInput } from '../components/DateTimeInputs';
import { useT } from '../lib/i18n';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { displayName, FLAGS, MISSING_LABELS, missingInfo, transferTime, type BookingDto, type BookingStatus, type RefItem } from '@bingo/shared';
import { api, ApiError } from '../lib/api';
import { tourTint } from '../lib/tourColor';
import { useRefData } from '../lib/reference';
import { addDays, longDate, shortDate, todayIso } from '../lib/dates';
import { BookingPanel, mayLeaveBookingPanel } from '../components/BookingPanel';
import { StatusChip } from '../components/StatusChip';
import { LangBadge } from '../components/LangBadge';
import { TransferBadge } from '../components/TransferBadge';
import { TransferTime } from '../components/TransferTime';

function BookingRow({ b, onOpen, onStatus, showDate, showTour }: {
  b: BookingDto; onOpen: () => void; onStatus?: (s: BookingStatus | null) => void; showDate?: boolean; showTour?: boolean;
}) {
  const { byId } = useRefData();
  const t = useT();
  const tour = byId('tours', b.tourId);
  const channel = byId('channels', b.channelId);
  const hotel = byId('hotels', b.hotelId);
  const missing = missingInfo(b);
  const flag = FLAGS.find((f) => f.key === b.flag);
  const route = b.transferDirection ? `${b.pickupPlace || b.pickupAddress || '?'} → ${b.dropoffPlace || b.dropoffAddress || '?'}` : '';
  const place = route || (hotel ? String(hotel.name) : b.hotelName || b.address || '');
  return (
    <div className={`brow ${b.status === 'X' ? 'cancelled' : ''} ${tour?.color ? 'tinted' : ''}`} style={tourTint(tour?.color)} role="button" tabIndex={0} onClick={onOpen}
      onKeyDown={(e) => e.key === 'Enter' && onOpen()}>
      <StatusChip status={b.status} onAdvance={onStatus} />
      <div className="brow-time mono">
        {b.transferDirection ? <TransferTime b={b} stacked /> : b.pickupTime ?? '--:--'}
      </div>
      <div className="brow-name">
        {flag && <span className="flag-dot" style={{ background: flag.color }} title={t(flag.label)} />}
        <b className="ellipsis">{displayName(b) || t('No name')}</b>
        {b.clientNameLatin && <span className="muted brow-orig ellipsis" lang="">{b.clientName}</span>}
      </div>
      <div className="brow-pax">{b.paxAdults}{b.paxChildren ? `+${b.paxChildren}` : ''}<span className="brow-unit"> {t('pax')}</span></div>
      <div className="brow-lang"><LangBadge code={b.language} /></div>
      <div className="brow-place ellipsis" title={place || undefined}>
        {showDate && <span className="muted">{shortDate(b.date)} · </span>}
        {(showDate || showTour) && tour && <span className="muted">{String(tour.code)} · </span>}
        {b.transferDirection && <TransferBadge d={b.transferDirection} checked={b.expectedChecked} />}
        {place || <span className="muted">{t('No pickup place')}</span>}
        {b.transferDirection && b.transportRef && <span className="mono muted brow-tr"> · {b.transportRef}</span>}
        {b.cashReminder ? <span className="brow-cash"> · {t('€{n} to collect', { n: b.cashReminder })}</span> : null}
      </div>
      <div className="brow-channel muted">{channel ? String(channel.code) : ''}</div>
      <div className="brow-phone mono ellipsis">{b.phone ?? ''}</div>
      <div className="brow-missing">{missing.map((m) => <span key={m} className="badge badge-warn">{t(MISSING_LABELS[m])}</span>)}</div>
    </div>
  );
}

/** Column titles above the dense list (computer only). */
function BookingHead() {
  const t = useT();
  return (
    <div className="card blist brow-headrow" aria-hidden="true">
      <div className="brow brow-labels">
        <span>{t('Status')}</span><span>{t('Time')}</span><span>{t('Client')}</span><span>{t('Pax')}</span><span>{t('Lang.')}</span>
        <span>{t('Pickup place')}</span><span>{t('Channel')}</span><span>{t('Phone')}</span><span>{t('Missing')}</span>
      </div>
    </div>
  );
}

export { BookingRow };


export function Bookings() {
  const [params, setParams] = useSearchParams();
  const date = params.get('date') ?? todayIso();
  const { lists, byId } = useRefData();
  const [bookings, setBookings] = useState<BookingDto[] | null>(null);
  const [error, setError] = useState('');
  const [open, setOpenRaw] = useState<BookingDto | 'new' | null>(null);
  /** Opening another booking (or a new one) asks first when the panel holds unsaved changes. */
  const setOpen = (v: BookingDto | 'new' | null) => { if (v !== open && mayLeaveBookingPanel()) setOpenRaw(v); };
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<BookingDto[] | null>(null);
  const t = useT();

  const load = useCallback(async () => {
    try {
      setBookings((await api<{ bookings: BookingDto[] }>(`/api/bookings?date=${date}`)).bookings);
      setError('');
    } catch (e) {
      setError((e as Error).message);
    }
  }, [date]);

  useEffect(() => {
    setBookings(null);
    void load();
    const onFocus = () => void load();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load]);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) return setResults(null);
    const timer = setTimeout(() => {
      api<{ bookings: BookingDto[] }>(`/api/bookings/search?q=${encodeURIComponent(q)}`).then((r) => setResults(r.bookings)).catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);

  const goTo = (d: string) => setParams(d === todayIso() ? {} : { date: d });

  const saved = (b: BookingDto) => {
    setResults((r) => r?.map((x) => (x.id === b.id ? b : x)) ?? r);
    setBookings((list) => {
      if (!list) return list;
      // A change never moves a booking by itself: it stays in place and only a new pickup time re-sorts
      // the list (stable sort, same order as the server: pickup time, then creation).
      if (b.date !== date) return list.filter((x) => x.id !== b.id);
      const next = list.some((x) => x.id === b.id) ? list.map((x) => (x.id === b.id ? b : x)) : [...list, b];
      return next.sort((a, c) => (a.pickupTime ?? '99').localeCompare(c.pickupTime ?? '99'));
    });
  };

  async function advance(b: BookingDto, status: BookingStatus | null) {
    try {
      const r = await api<{ booking: BookingDto }>(`/api/bookings/${b.id}`, { method: 'PATCH', body: { version: b.version, status } });
      saved(r.booking);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('Could not change the status.'));
      void load();
    }
  }

  const groups = useMemo(() => {
    const live = (bookings ?? []).filter((b) => b.status !== 'X' && !b.transferDirection);
    const order = new Map((lists?.tours ?? []).map((t, i) => [t.id, i] as const));
    const byTour = new Map<string, BookingDto[]>();
    for (const b of live) {
      const k = b.tourId ?? 'none';
      byTour.set(k, [...(byTour.get(k) ?? []), b]);
    }
    return [...byTour.entries()].sort(([a], [c]) => (order.get(a) ?? 9999) - (order.get(c) ?? 9999));
  }, [bookings, lists]);

  const cancelled = (bookings ?? []).filter((b) => b.status === 'X');
  const live = (bookings ?? []).filter((b) => b.status !== 'X');
  const transfers = live.filter((b) => !!b.transferDirection).sort((a, c) => (transferTime(a) ?? '99').localeCompare(transferTime(c) ?? '99'));
  const totals = {
    bookings: live.length,
    pax: live.reduce((s, b) => s + b.pax, 0),
    missing: live.filter((b) => missingInfo(b).length).length,
    notCC: live.filter((b) => b.status !== 'CC').length,
  };

  const tourTitle = (id: string) => {
    const tr: RefItem | undefined = byId('tours', id === 'none' ? null : id);
    return tr ? `${String(tr.code)} · ${String(tr.name)}` : t('No tour chosen');
  };

  return (
    <div className="stack">
      <div className="page-head">
        <h1>{t('Bookings')}</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <Link className="btn" to="/bookings/missing">{t('Missing info')}</Link>
          <button className="btn btn-primary" onClick={() => setOpen('new')}>{t('New booking')}</button>
        </div>
      </div>

      <input className="input" type="search" placeholder={t('Search any date: client, phone, email, reference')} value={query}
        onChange={(e) => setQuery(e.target.value)} aria-label={t('Search bookings')} />

      {results ? (
        <div className="stack" style={{ gap: 8 }}>
          <div className="muted">{results.length === 50 ? t('First 50 results, nearest date first') : t('{n} found, nearest date first', { n: results.length })}</div>
          <div className="card blist">
            {results.map((b) => <BookingRow key={b.id} b={b} showDate onOpen={() => setOpen(b)} />)}
            {results.length === 0 && <div className="muted" style={{ padding: 16 }}>{t('Nothing found.')}</div>}
          </div>
        </div>
      ) : (
        <>
          <div className="datebar">
            <button className="btn btn-sm" onClick={() => goTo(addDays(date, -1))} aria-label={t('Previous day')}>‹</button>
            <DateInput value={date} onChange={(d) => goTo(d)} required aria-label={t('Date')} />
            <button className="btn btn-sm" onClick={() => goTo(addDays(date, 1))} aria-label={t('Next day')}>›</button>
            {date !== todayIso() && <button className="btn btn-sm" onClick={() => goTo(todayIso())}>{t('Today')}</button>}
            <h2 className="datebar-title">{longDate(date)}</h2>
          </div>

          <div className="kpis">
            <div className="kpi"><b>{totals.bookings}</b><span>{totals.bookings === 1 ? t('booking') : t('bookings')}</span></div>
            <div className="kpi"><b>{totals.pax}</b><span>{t('pax')}</span></div>
            <div className={`kpi ${totals.notCC ? 'kpi-warn' : ''}`}><b>{totals.notCC}</b><span>{t('not CC yet')}</span></div>
            <div className={`kpi ${totals.missing ? 'kpi-warn' : ''}`}><b>{totals.missing}</b><span>{t('missing info')}</span></div>
            {transfers.length > 0 && <a className="kpi kpi-btn kpi-transfer" href="#transfers"><b>{transfers.length}</b><span>{t('transfers')}</span></a>}
            {cancelled.length > 0 && <div className="kpi"><b>{cancelled.length}</b><span>{t('cancelled')}</span></div>}
          </div>

          {error && <div className="alert alert-bad">{error}</div>}
          {!bookings ? <div className="muted">{t('Loading…')}</div> : bookings.length === 0 ? (
            <div className="card section muted">{t('No booking on this day.')}</div>
          ) : (
            <div className="stack blist-stack">
              <BookingHead />
              {groups.map(([tourId, list]) => (
                <div key={tourId} className="card blist">
                  <div className={`blist-head ${byId('tours', tourId === 'none' ? null : tourId)?.color ? 'tinted' : ''}`} style={tourTint(byId('tours', tourId === 'none' ? null : tourId)?.color)}>
                    <b>{tourTitle(tourId)}</b>
                    <span className="muted">{list.length === 1 ? t('1 booking') : t('{n} bookings', { n: list.length })} · {list.reduce((s, b) => s + b.pax, 0)} {t('pax')}</span>
                  </div>
                  {list.map((b) => <BookingRow key={b.id} b={b} onOpen={() => setOpen(b)} onStatus={(s) => advance(b, s)} />)}
                </div>
              ))}
              {transfers.length > 0 && (
                <div id="transfers" className="card blist blist-transfers">
                  <div className="blist-head transfer-head">
                    <b>{t('Transfers')}</b>
                    <span className="muted">{transfers.length === 1 ? t('1 booking') : t('{n} bookings', { n: transfers.length })} · {transfers.reduce((s, b) => s + b.pax, 0)} {t('pax')}</span>
                  </div>
                  {transfers.map((b) => <BookingRow key={b.id} b={b} onOpen={() => setOpen(b)} onStatus={(s) => advance(b, s)} />)}
                </div>
              )}
              {cancelled.length > 0 && (
                <details className="card blist">
                  <summary className="blist-head"><b>{t('Cancelled')}</b><span className="muted">{t('{n} · not counted anywhere', { n: cancelled.length })}</span></summary>
                  {cancelled.map((b) => <BookingRow key={b.id} b={b} onOpen={() => setOpen(b)} onStatus={(s) => advance(b, s)} />)}
                </details>
              )}
            </div>
          )}
        </>
      )}

      {open && (
        <BookingPanel key={open === 'new' ? 'new' : open.id} booking={open === 'new' ? null : open} date={date}
          onClose={() => setOpenRaw(null)} onSaved={saved} />
      )}
    </div>
  );
}
